import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { cx } from '@platform/core/lib/cx';
import { groupValues, stagesByFor } from '@platform/core/lib/viewSettings';
import { optionColor, statusColor } from '@platform/core/lib/statusColors';
import type { IRecordViewProps } from '@platform/core/types';
import { facetFieldOf } from '../menu/facetEntry';
import { stagePanelId, stageTabId } from './stageIds';
import { countsOf, selectedStage } from './stageSelection';
import { useListStats } from './useListStats';
import './stages.css';

/**
 * Вкладки подання «Етапи» (UIUX views/view-switcher; прототип v4): значення поля settings.stages.by (типово status) або поля пункту меню
 * «За …» (`?item=facet:<поле>` — лише значення із записами) — крапка кольору, підпис, лічильник у межах фільтрів списку (пункт меню, пошук,
 * швидкі й розширені фільтри); вкладки «Усі» немає — типово перша вкладка із записами; вибрана вкладка запам'ятовується в стані пункту меню
 * й фільтрує таблицю під вкладками.
 */
export function StageTabs({ model }: IRecordViewProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const [params] = useSearchParams();
  const facet = facetFieldOf(params.get('item'));
  const by = facet ?? stagesByFor(model.rt);
  const { data: stats } = useListStats(model, by);
  const counts = countsOf(stats?.buckets);
  const all = groupValues(model.rt, by);
  const values = facet && stats ? all.filter((v) => (counts.get(v.value) ?? 0) > 0) : all;
  const field = model.rt.fields.find((f) => f.key === by) ?? { key: by, options: all };
  const current = selectedStage(values.map((v) => v.value), counts, model.state.stage);
  const ready = stats !== undefined;
  const { setState } = model;
  useEffect(() => { if (ready && current !== model.state.stage) setState({ stage: current }); }, [ready, current, model.state.stage, setState]);
  return (
    <div className="lv-stages" role="tablist" aria-label={t('views.stages')}>
      {values.map((v) => (
        <button key={v.value} id={stageTabId(model.rt.key, v.value)} type="button" role="tab" aria-selected={v.value === current}
          aria-controls={v.value === current ? stagePanelId(model.rt.key) : undefined} className={cx('lv-stages__tab', v.value === current && 'is-on')}
          onClick={() => setState({ stage: v.value })}>
          <span className="lv-stages__dot" style={{ background: by === 'status' ? statusColor(model.rt.statuses, v.value, model.rt.key) : optionColor(field, v.value, model.rt.key) }} />
          <span>{strings.option(model.rt.key, by, v.value, v.label)}</span>
          <span className="lv-stages__count">{counts.get(v.value) ?? 0}</span>
        </button>
      ))}
    </div>
  );
}
